import React, { useRef, useEffect, useState, useContext } from "react";
import Typed from "typed.js";
import { AnimateSharedLayout, motion } from "framer-motion/dist/framer-motion";
import ContactTypeDisplay from "./ContactTypeDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhoneAlt } from "@fortawesome/free-solid-svg-icons";
import { headingVariant, containerVariant, itemVariant } from "./PageVariants";
import { NavContext } from "./App";
import { InView } from "react-intersection-observer";
import ContactForm from "./ContactForm";

export default function Contact({ pageRef }) {
	const typedElementForSub = useRef(null);
	const typedElementForHeading = useRef(null);
	const setSelectedNav = useContext(NavContext);
	const [isInView, setIsInView] = useState(false);

	const contactTypes = ["CONTACT FORM"]; // 'EMAIL', 'PHONE'
	const [selectedContactType, setSelectedContactType] = useState("CONTACT FORM");

	useEffect(() => {
		let typedSub = new Typed(typedElementForSub.current, {
			strings: ["INFO TO"],
			startDelay: 0,
			typeSpeed: 40,
			showCursor: false,
		});

		let typedHeading = new Typed(typedElementForHeading.current, {
			strings: ["CONTACT ME"],
			startDelay: 800,
			typeSpeed: 80,
			showCursor: false,
		});

		return () => {
			typedSub.destroy();
			typedHeading.destroy();
		};
	}, [isInView]);

	const viewChange = (inView) => {
		setIsInView(inView);
		if (inView) {
			setSelectedNav({ Heading: "CONTACT" });
		}
	};

	return (
		<InView threshold={0.25} onChange={(inView, entry) => viewChange(inView)}>
			<div
				ref={pageRef}
				className="w-full md:w-10/12 m-auto grid grid-cols-1 justify-start items-start"
			>
				<motion.h4
					variants={headingVariant}
					initial="hide"
					animate={isInView ? "show" : "hide"}
					exit="hide"
					className="text-3xl text-green-600 border-l-8 border-green-600 pl-4 pt-6 h-24 tracking-wider"
				>
					<span
						className="text-gray-600 dark:text-gray-400 text-sm"
						ref={typedElementForSub}
					></span>
					<br />
					<span className="text-green-600" ref={typedElementForHeading}></span>
				</motion.h4>
				<motion.div
					variants={containerVariant}
					initial="hide"
					animate="show"
					className="text-gray-500 p-3 md:p-4 lg:p-10 my-8 bg-white rounded-3xl text-xs dark:text-gray-200 dark:bg-gray-600"
				>
					<AnimateSharedLayout>
						<motion.div
							variants={itemVariant}
							className="flex gap-8 justify-center items-center"
						>
							{contactTypes &&
								contactTypes.map((item) => (
									<ContactTypeDisplay
										key={item}
										contactType={item}
										isSelected={selectedContactType === item}
										onClick={() => setSelectedContactType(item)}
									/>
								))}
						</motion.div>
					</AnimateSharedLayout>
					<div className="flex justify-center items-center p-0 mt-6 h-full">
						<div className="rounded-xl py-4 w-full m-auto text-center space-y-4">
							{selectedContactType && selectedContactType === "CONTACT FORM" && (
								<div className="w-full text-base h-full">
									<ContactForm />
								</div>
							)}
							{selectedContactType && selectedContactType === "EMAIL" && (
								<div className="w-full md:w-1/2 m-auto">
									<FontAwesomeIcon
										icon={faEnvelope}
										size="6x"
										className="m-2 text-green-600 p-4 bg-green-100 rounded-lg"
									/>
									<p className="text-sm"> Please email me at, </p>
									<a
										className="text-xl text-green-600 whitespace-nowrap"
										href="mailto:johndoetest@gmail.com"
										aria-label="portfolio-email"
									>
										johndoetest@gmail.com
									</a>
									<p className="text-base font-semibold italic">
										Please note that above mentioned is a test email. Please
										submit your message via the form to contact me and I will
										get in touch with you ASAP
									</p>
								</div>
							)}
							{selectedContactType && selectedContactType === "PHONE" && (
								<div className="w-full md:w-1/2 m-auto text-center">
									<FontAwesomeIcon
										icon={faPhoneAlt}
										size="6x"
										className="m-2 text-green-600 p-4 bg-green-100 rounded-lg"
									/>
									<p className="text-sm"> Please call me at, </p>
									<a
										className="text-xl text-green-600 whitespace-nowrap"
										href="tel:+1-847-555-TEST"
										aria-label="portfolio-phone-number"
									>
										+1 (847) 555-TEST
									</a>
									<p className="text-base font-semibold italic">
										Please note that above mentioned is a test phone number.
										Please submit your message via the form to contact me and I
										will get in touch with you ASAP
									</p>
								</div>
							)}
						</div>
					</div>
				</motion.div>
			</div>
		</InView>
	);
}
